import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useConfig } from "../lib/ConfigContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Crown, CheckCircle2, Star, Phone, MessageSquare, BadgeCheck, Lock, Calendar } from "lucide-react";

export default function DealerPlanCard({ onChange }) {
  const [me, setMe] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const { plans: cfgPlans, settings } = useConfig();

  // SSOT — dealer plan catalog now comes from ConfigContext (admin_plans collection).
  // Fall back to /dealer/my-plan response for backwards compat if context is empty.
  const ctxCatalog = (cfgPlans?.dealer || []).map((p) => ({
    ...p,
    highlights: p.highlights && p.highlights.length ? p.highlights : (p.features || []),
  }));

  const load = async () => {
    try {
      const r = await api.get("/dealer/my-plan");
      setMe(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const requestUpgrade = async (planId) => {
    setRequesting(planId);
    try {
      // Self-service is admin-granted for dealer plans (no Razorpay flow yet).
      // Send a notification by calling the existing magic-link "contact admin" workflow proxy.
      await api.post("/enquiries", {
        property_id: "_dealer_plan_request",
        name: "Dealer Plan Request",
        phone: "0",
        message: `Plan upgrade requested: ${planId}`,
      }).catch(() => {});
      toast.success(`Request sent for ${planId.replace("dealer_", "")}. Admin will activate within 24h.`);
    } catch (e) {
      toast.error("Failed to send request");
    }
    setRequesting(null);
  };

  if (!me) return null;
  const plan = me.plan || {};
  const isActive = me.active;
  const expiresIn = plan.expires_at
    ? Math.max(0, Math.floor((new Date(plan.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  // Prefer SSOT context catalog; fall back to API response (covers admin viewing dealer flow when context lacks dealer plans).
  const catalog = ctxCatalog.length ? ctxCatalog : (me.catalog || []);
  const plansDisabled = settings && settings.plans_enabled === false;

  return (
    <div className="space-y-5" data-testid="dealer-plan-card">
      {isActive ? (
        <div className="bg-gradient-to-br from-[#065F46] to-[#047857] text-white rounded-sm p-6 lg:p-8" data-testid="active-plan-card">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/70 mb-2 inline-flex items-center gap-1.5">
            <CheckCircle2 size={11} /> Active subscription
          </div>
          <h3 className="font-heading font-bold text-3xl tracking-tight">{plan.name || "Dealer plan"}</h3>
          <div className="flex items-center gap-4 mt-3 text-[13px] text-white/85 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> Expires {plan.expires_at ? new Date(plan.expires_at).toLocaleDateString() : "—"}</span>
            {expiresIn !== null && <span className="inline-flex items-center gap-1.5"><Crown size={12} /> {expiresIn} days remaining</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5 pt-5 border-t border-white/20" data-testid="active-plan-perks">
            <PerkChip icon={<Phone size={11} />} label="Contacts" />
            <PerkChip icon={<MessageSquare size={11} />} label="Leads" />
            <PerkChip icon={<Star size={11} />} label="Priority" />
            <PerkChip icon={<BadgeCheck size={11} />} label="Badge" />
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="no-plan-card">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#52525B] mb-1 inline-flex items-center gap-1.5">
            <Lock size={11} /> No active plan
          </div>
          <h3 className="font-heading font-bold text-xl tracking-tight">You're on the free tier</h3>
          <p className="text-[12px] text-[#71717A] mt-1">
            Browsing and submitting listings is always free. Choose a plan below to unlock contact details, leads inbox, and priority placement.
          </p>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-2">Available plans</div>
        {plansDisabled ? (
          <div className="bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] rounded-sm p-4 text-[13px]" data-testid="dealer-plans-disabled">
            Plans are currently disabled by admin. You can keep submitting listings on the free tier.
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dealer-plans-grid">
          {catalog.map((p) => {
            const current = isActive && plan.plan_id === p.id;
            return (
              <div
                key={p.id}
                className={`relative bg-white rounded-sm p-5 border ${p.recommended ? "border-[#0055FF] ring-1 ring-[#0055FF]" : "border-[#E4E4E7]"}`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.recommended && (
                  <span className="absolute -top-2 right-4 bg-[#0055FF] text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm">
                    Recommended
                  </span>
                )}
                {current && (
                  <span className="absolute -top-2 left-4 bg-[#065F46] text-white text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm">
                    Current
                  </span>
                )}
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#52525B]">{p.name}</div>
                <div className="font-heading font-bold text-3xl tracking-tight text-[#09090B] mt-1">
                  ₹{(p.price_inr || 0).toLocaleString("en-IN")}
                  <span className="text-[12px] font-normal text-[#A1A1AA] ml-1.5">/ {p.duration_days} days</span>
                </div>
                <ul className="mt-4 space-y-1.5 text-[12px] text-[#52525B]">
                  {(p.highlights || []).map((h, i) => (
                    <li key={`${h}-${i}`} className="inline-flex items-start gap-1.5">
                      <CheckCircle2 size={11} className="text-[#0055FF] mt-0.5 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full rounded-sm mt-4 font-semibold ${current ? "bg-[#F4F4F5] text-[#52525B] hover:bg-[#F4F4F5] cursor-default" : "btn-brand"}`}
                  disabled={current || requesting === p.id}
                  onClick={() => !current && requestUpgrade(p.id)}
                  data-testid={`plan-cta-${p.id}`}
                >
                  {current ? "Current plan" : requesting === p.id ? "Sending…" : "Request upgrade"}
                </Button>
              </div>
            );
          })}
        </div>
        )}
        <p className="text-[11px] text-[#A1A1AA] mt-3">
          <Lock size={10} className="inline -mt-0.5 mr-1" /> Plan activation is admin-managed. Click "Request upgrade" — admin will reach out within 24 hours.
        </p>
      </div>
    </div>
  );
}

const PerkChip = ({ icon, label }) => (
  <div className="bg-white/10 border border-white/20 rounded-sm px-2 py-1.5 text-[11px] inline-flex items-center gap-1.5">
    {icon} <span className="font-semibold">{label}</span>
  </div>
);
